import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useLang } from "../i18n";
import { CheckCircle2, MessageCircle } from "lucide-react";

const TIMES = ["10:00", "11:30", "14:00", "16:00", "17:30"];
const PEOPLE = [1, 2, 3, 4, 5, 6, 7, 8];
const TIERS = [
  { id: "essentials", price: 14 },
  { id: "intimate", price: 20 },
  { id: "signature", price: 26 },
];

function toLocalDateString(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const { t, lang } = useLang();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("11:30");
  const [people, setPeople] = useState(2);
  const [language, setLanguage] = useState(lang);
  const [tier, setTier] = useState(() => localStorage.getItem("porto_preselect_tier") || "intimate");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [notes, setNotes] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [blocked, setBlocked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const month = useMemo(() => {
    const d = date || new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, [date]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/bookings/availability`, { params: { month } });
        if (active) setBlocked(data.blocked || []);
      } catch {}
    })();
    return () => { active = false; };
  }, [month]);

  useEffect(() => { setLanguage(lang); }, [lang]);

  useEffect(() => {
    const handler = (e) => { if (e.detail) setTier(e.detail); };
    window.addEventListener("porto:preselect-tier", handler);
    return () => window.removeEventListener("porto:preselect-tier", handler);
  }, []);

  const tierPrice = TIERS.find((tt) => tt.id === tier)?.price || 14;
  const total = tierPrice * people;

  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0,0,0,0);
    return [{ before: today }, ...blocked.map((b) => new Date(`${b}T12:00:00`))];
  }, [blocked]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error(t.booking.pickDate); return; }
    if (!name || !email || !country || !whatsapp) { toast.error(t.common.required); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name, email, country, whatsapp,
        date: toLocalDateString(date), time, people,
        language, tier, notes, website,
      });
      setDone(true);
      toast.success(t.booking.success);
      try { localStorage.removeItem("porto_preselect_tier"); } catch {}
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-system" className="py-24 md:py-32 bg-[#FDFAF3] border-y border-[#E5D9C0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] text-[#5E7891] mb-5">{t.booking.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[#2D3640]">{t.booking.title}</h2>
          <p className="mt-5 text-base md:text-lg text-[#4A5A6B] max-w-xl">{t.booking.sub}</p>
        </div>

        {/* Prefer to talk directly — WhatsApp */}
        <div data-testid="booking-whatsapp-banner" className="mt-10 rounded-[24px] border border-[#E5D9C0] bg-[#F1E6D6] p-6 md:p-7 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
          <div>
            <p className="font-serif-display text-2xl md:text-3xl text-[#2D3640]">{t.booking.preferTalk}</p>
            <p className="mt-1 text-sm text-[#5E7891]">WhatsApp · +351 931 777 823</p>
          </div>
          <a data-testid="booking-whatsapp-cta"
            href="https://wa.me/351931777823?text=Hello%21%20I%20would%20like%20information%20about%20Porto%20Beyond%20the%20Postcard%20and%20available%20tour%20dates."
            target="_blank" rel="noreferrer"
            className="btn-sweep inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#D8B07A] text-[#2D3640] text-sm tracking-wider hover:bg-[#5E7891] hover:text-[#FDFAF3] transition-colors shrink-0">
            <MessageCircle size={16} strokeWidth={1.7} /> {t.booking.quoteWhatsapp}
          </a>
        </div>

        {done ? (
          <div data-testid="booking-success" className="mt-14 max-w-2xl mx-auto text-center p-10 md:p-12 rounded-[28px] bg-[#F1E6D6] border border-[#E5D9C0]">
            <CheckCircle2 size={40} className="mx-auto text-[#D8B07A]" strokeWidth={1.4} />
            <h3 className="font-serif-display text-3xl md:text-4xl mt-5 text-[#2D3640] leading-snug">{t.booking.successTitle}</h3>
            <p className="mt-5 text-base md:text-lg text-[#4A5A6B] leading-relaxed">{t.booking.successBody}</p>
          </div>
        ) : (
          <form data-testid="booking-form" onSubmit={submit} className="mt-14 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 p-6 md:p-8 rounded-[24px] bg-[#F7F1E8] border border-[#E5D9C0]">
              <Label className="block text-xs uppercase tracking-[0.22em] text-[#5E7891] mb-3">{t.booking.pickDate}</Label>
              <div data-testid="booking-calendar" className="flex justify-center rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0] p-3">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  weekStartsOn={1}
                />
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.time}</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="booking-time" className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMES.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.people}</Label>
                  <Select value={String(people)} onValueChange={(v) => setPeople(parseInt(v))}>
                    <SelectTrigger data-testid="booking-people" className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PEOPLE.map((p) => <SelectItem key={p} value={String(p)}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.language}</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="booking-language" className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.tier}</Label>
                  <Select value={tier} onValueChange={setTier}>
                    <SelectTrigger data-testid="booking-tier" className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {t.pricing.tiers.map((tt) => (
                        <SelectItem key={tt.id} value={tt.id}>{tt.name} · €{tt.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 p-6 md:p-8 rounded-[24px] bg-[#F1E6D6] border border-[#E5D9C0]">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.name}</Label>
                  <Input data-testid="booking-name" value={name} onChange={(e)=>setName(e.target.value)} required className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.email}</Label>
                  <Input data-testid="booking-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.country}</Label>
                    <Input data-testid="booking-country" value={country} onChange={(e)=>setCountry(e.target.value)} required className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.whatsapp}</Label>
                    <Input data-testid="booking-whatsapp" value={whatsapp} onChange={(e)=>setWhatsapp(e.target.value)} required className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.notes}</Label>
                  <Textarea data-testid="booking-notes" rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} className="mt-2 bg-[#FDFAF3] border-[#E5D9C0]" />
                </div>
                {/* honeypot */}
                <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e)=>setWebsite(e.target.value)}
                  className="hidden" aria-hidden="true" />
              </div>

              <div className="mt-6 p-5 rounded-2xl bg-[#FDFAF3] border border-[#E5D9C0]">
                <p className="text-xs uppercase tracking-[0.22em] text-[#5E7891]">{t.booking.summary}</p>
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-sm text-[#4A5A6B]">€{tierPrice} {t.booking.perPerson} × {people}</span>
                  <span className="font-serif-display text-3xl text-[#2D3640]" data-testid="booking-total">€{total}</span>
                </div>
                <p className="font-script text-lg text-[#5E7891] mt-1">{t.booking.pending}</p>
              </div>

              <button data-testid="booking-submit" type="submit" disabled={submitting}
                className="btn-sweep mt-6 w-full inline-flex items-center justify-center px-6 py-3.5 rounded-full bg-[#2D3640] text-[#FDFAF3] text-sm tracking-wider hover:bg-[#5E7891] transition-colors disabled:opacity-60">
                {submitting ? "..." : t.booking.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
